import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import uz from '../locales/uz';
import ru from '../locales/ru';
import en from '../locales/en';
import trLocale from '../locales/tr';

// Supported UI languages. `uz` = Uzbek Latin (also the fallback source),
// `ru` = Russian, `en` = English, `tr` = Turkish.
export const LANGS = ['uz', 'ru', 'en', 'tr'];
export const DEFAULT_LANG = 'uz';

const translations = { uz, ru, en, tr: trLocale };

// Resolve a key against a translations object. Supports both flat keys
// ('save') and dotted paths ('login_page.server') so files can be organised
// either way without breaking callers.
function resolve(obj, key) {
  if (obj == null) return undefined;
  if (obj[key] !== undefined) return obj[key];            // flat fast-path
  return key.split('.').reduce(
    (acc, part) => (acc == null ? undefined : acc[part]),
    obj
  );
}

// Look up a translation. Fallback chain: requested lang → uz (Latin) → the key
// itself (so a missing key shows the key, never `undefined`).
export const t = (key, lang = DEFAULT_LANG) => {
  const hit = resolve(translations[lang], key);
  if (hit !== undefined) return hit;
  const fb = resolve(translations[DEFAULT_LANG], key);
  return fb !== undefined ? fb : key;
};

// Non-hook variant for module-level helpers / store slices: language is read from localStorage.
export const tr = (key, vars) => {
  let lang = DEFAULT_LANG;
  try { lang = localStorage.getItem('language') || DEFAULT_LANG; } catch { /* ignore */ }
  let str = t(key, lang);
  if (vars && typeof str === 'string') {
    str = str.replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m));
  }
  return str;
};

// Hook: returns a `t(key, vars?)` bound to the current UI language from Redux.
// `vars` does simple {name}-style interpolation, e.g. t('greet', { name }).
export function useT() {
  const lang = useSelector((s) => s.lang?.current) || DEFAULT_LANG;
  // Стабильная ссылка на функцию (меняется только при смене языка) — безопасно для deps хуков.
  return useCallback((key, vars) => {
    let str = t(key, lang);
    if (vars && typeof str === 'string') {
      str = str.replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m));
    }
    return str;
  }, [lang]);
}
